import React, { useCallback, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { Button as ButtonAntd, Checkbox, Input, Radio } from 'antd'
import { createSimpleStore } from 'react-simple-reducer'
import { useHistory } from 'react-router-dom'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import api from '../../api'
import { TextFieldInput } from '../../components/TextField'

interface MatriculaSelecionada {
  id: number
  alunoNome: string
  cursoNomeReduzido: string
  alunoCpf?: string
  valorPago?: number
}

const initialState = {
  matriculasSelecionadas: [] as MatriculaSelecionada[],
  loadingMatriculas: false,
}

const NotaFiscalNovaStore = createSimpleStore(
  initialState,
  {
    addMatriculas: (state, matriculas: MatriculaSelecionada[]) => {
      const existingIds = new Set(state.matriculasSelecionadas.map((m) => m.id))
      const novasMatriculas = matriculas.filter((m) => !existingIds.has(m.id))
      return {
        ...state,
        matriculasSelecionadas: [...state.matriculasSelecionadas, ...novasMatriculas],
      }
    },
    removeMatricula: (state, id: number) => ({
      ...state,
      matriculasSelecionadas: state.matriculasSelecionadas.filter((m) => m.id !== id),
    }),
    setLoadingMatriculas: (state, loadingMatriculas: boolean) => ({ ...state, loadingMatriculas }),
  },
  { thunks: {} }
)

export function NotaFiscalNova() {
  const init = useCallback(() => {}, [])

  return (
    <NotaFiscalNovaStore.Provider init={init}>
      <Layout>
        <Container>
          <Header />
          <SeletorMatriculas />
          <FormularioNotaFiscal />
        </Container>
      </Layout>
    </NotaFiscalNovaStore.Provider>
  )
}

function Header() {
  const history = useHistory()

  return (
    <HeaderRow>
      <ButtonAntd onClick={() => history.push('/notas-fiscais')}>← Voltar</ButtonAntd>
      <Title>Nova Nota Fiscal</Title>
    </HeaderRow>
  )
}

function SeletorMatriculas() {
  const { matriculasSelecionadas, loadingMatriculas } = NotaFiscalNovaStore.useState()
  const dispatch = NotaFiscalNovaStore.useDispatch()
  const [tipoSeletor, setTipoSeletor] = useState<'matricula' | 'turma'>('matricula')
  const [inputId, setInputId] = useState('')

  const handleAdicionar = async () => {
    if (!inputId) return
    const id = Number(inputId)
    if (!id) return toast.error('ID inválido')

    dispatch(NotaFiscalNovaStore.actions.setLoadingMatriculas(true))
    try {
      if (tipoSeletor === 'matricula') {
        const matricula = await api.Matriculas.get(id)
        if (!matricula) throw new Error('Matrícula não encontrada')
        dispatch(
          NotaFiscalNovaStore.actions.addMatriculas([
            {
              id: matricula.id,
              alunoNome: matricula.aluno?.nome || '-',
              cursoNomeReduzido: matricula.curso?.nomeReduzido || '-',
              alunoCpf: matricula.aluno?.cpf,
              valorPago: matricula.pagamento?.valor,
            },
          ])
        )
      } else {
        const matriculas = await api.NotasFiscais.getTurmaMatriculas(id)
        if (!matriculas.length) throw new Error('Turma sem matrículas')
        dispatch(NotaFiscalNovaStore.actions.addMatriculas(matriculas))
      }
      setInputId('')
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Erro ao buscar matrícula')
    } finally {
      dispatch(NotaFiscalNovaStore.actions.setLoadingMatriculas(false))
    }
  }

  return (
    <SeletorContainer>
      <SeletorTitle>Matrículas</SeletorTitle>
      <SeletorRow>
        <Radio.Group
          value={tipoSeletor}
          onChange={(e) => setTipoSeletor(e.target.value)}
          style={{ marginRight: 12 }}
        >
          <Radio.Button value="matricula">Matrícula ID</Radio.Button>
          <Radio.Button value="turma">Turma ID</Radio.Button>
        </Radio.Group>
        <Input
          placeholder={tipoSeletor === 'matricula' ? 'ID da matrícula' : 'ID da turma'}
          value={inputId}
          onChange={(e) => setInputId(e.target.value)}
          onPressEnter={handleAdicionar}
          style={{ width: 180, marginRight: 8 }}
        />
        <ButtonAntd type="primary" onClick={handleAdicionar} loading={loadingMatriculas}>
          Adicionar
        </ButtonAntd>
      </SeletorRow>
      <MatriculasBadgesRow>
        {matriculasSelecionadas.length === 0 && (
          <EmptyMatriculas>
            Nenhuma matrícula selecionada — toda a nota será salva como crédito
          </EmptyMatriculas>
        )}
        {matriculasSelecionadas.map((m) => (
          <MatriculaBadge key={m.id}>
            <span>
              #{m.id} — {m.alunoNome} — {m.cursoNomeReduzido}
            </span>
            <RemoveButton
              onClick={() => dispatch(NotaFiscalNovaStore.actions.removeMatricula(m.id))}
            >
              ×
            </RemoveButton>
          </MatriculaBadge>
        ))}
      </MatriculasBadgesRow>
    </SeletorContainer>
  )
}

function FormularioNotaFiscal() {
  const { matriculasSelecionadas } = NotaFiscalNovaStore.useState()
  const history = useHistory()
  const [tipoDocumento, setTipoDocumento] = useState<'cpf' | 'cnpj'>('cnpj')
  const [loadingCnpj, setLoadingCnpj] = useState(false)
  const [loadingCep, setLoadingCep] = useState(false)

  const formik = useFormik({
    initialValues: {
      tomadorCpfCnpj: '',
      tomadorNome: '',
      valorNotaFiscal: '',
      descricaoNota: '',
      informacoesComplementares: '',
      incluirRetencaoCsrf: false,
      tomadorEndereco: {
        cep: '',
        logradouro: '',
        numero: '',
        bairro: '',
        cidade: '',
        codigoCidade: '',
        estado: '',
      },
    },
    validate: (values) => {
      const errors: any = {}
      if (!values.tomadorCpfCnpj) errors.tomadorCpfCnpj = 'Obrigatório'
      if (!values.tomadorNome) errors.tomadorNome = 'Obrigatório'
      if (!values.valorNotaFiscal) errors.valorNotaFiscal = 'Obrigatório'
      if (!values.descricaoNota) errors.descricaoNota = 'Obrigatório'
      return errors
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const valorNotaFiscal = Number(
          String(values.valorNotaFiscal)
            .replace(/[^\d,-]/g, '')
            .replace(',', '.')
        )
        if (matriculasSelecionadas.length === 0) {
          const confirmed = window.confirm(
            'Nenhuma matrícula selecionada. O valor será salvo como crédito. Confirmar?'
          )
          if (!confirmed) return
        }
        await api.NotasFiscais.emitir({
          matriculasIds: matriculasSelecionadas.map((m) => m.id),
          tomadorCpfCnpj: values.tomadorCpfCnpj,
          tomadorNome: values.tomadorNome,
          valorNotaFiscal,
          descricaoNota: values.descricaoNota,
          informacoesComplementares: values.informacoesComplementares,
          incluirRetencaoCsrf: values.incluirRetencaoCsrf,
          tomadorEndereco: values.tomadorEndereco,
        })
        toast.success('Nota fiscal emitida com sucesso!')
        history.push('/notas-fiscais')
      } catch (error: any) {
        toast.error(error?.response?.data?.message || 'Erro ao emitir nota fiscal')
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (tipoDocumento !== 'cpf' || matriculasSelecionadas.length !== 1) return
    const matricula = matriculasSelecionadas[0]
    if (!matricula.alunoCpf) return
    formik.setFieldValue('tomadorCpfCnpj', matricula.alunoCpf)
    formik.setFieldValue('tomadorNome', matricula.alunoNome)
    if (matricula.valorPago)
      formik.setFieldValue('valorNotaFiscal', String(matricula.valorPago).replace('.', ','))
  }, [tipoDocumento, matriculasSelecionadas])

  useEffect(() => {
    const cnpjLimpo = formik.values.tomadorCpfCnpj.replace(/[.\-/]/g, '')
    if (tipoDocumento !== 'cnpj' || cnpjLimpo.length !== 14) return
    const timer = setTimeout(async () => {
      try {
        setLoadingCnpj(true)
        const data = await api.NotasFiscais.buscarCnpj(cnpjLimpo)
        formik.setFieldValue('tomadorNome', data.razao_social || formik.values.tomadorNome)
        formik.setFieldValue('tomadorEndereco', {
          cep: data.cep || '',
          logradouro: data.logradouro || '',
          numero: data.numero || '',
          bairro: data.bairro || '',
          cidade: data.municipio || '',
          codigoCidade: data.codigo_municipio_ibge || '',
          estado: data.uf || '',
        })
        toast.success('Dados do CNPJ carregados!')
      } catch {
        // silently fail
      } finally {
        setLoadingCnpj(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [formik.values.tomadorCpfCnpj, tipoDocumento])

  useEffect(() => {
    const cepLimpo = formik.values.tomadorEndereco.cep.replace(/[.\-]/g, '')
    if (cepLimpo.length !== 8) return
    const timer = setTimeout(async () => {
      try {
        setLoadingCep(true)
        const data = await api.NotasFiscais.buscarCep(cepLimpo)
        if (data.erro) return toast.error('CEP não encontrado')
        formik.setFieldValue('tomadorEndereco', {
          ...formik.values.tomadorEndereco,
          logradouro: data.logradouro || formik.values.tomadorEndereco.logradouro,
          bairro: data.bairro || formik.values.tomadorEndereco.bairro,
          cidade: data.localidade || formik.values.tomadorEndereco.cidade,
          estado: data.uf || formik.values.tomadorEndereco.estado,
          codigoCidade: data.ibge || formik.values.tomadorEndereco.codigoCidade,
        })
        toast.success('Endereço carregado do CEP!')
      } catch {
        // silently fail
      } finally {
        setLoadingCep(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [formik.values.tomadorEndereco.cep])

  return (
    <FormContainer onSubmit={formik.handleSubmit}>
      <FormGrid>
        <div className="entire-col">
          <Radio.Group
            value={tipoDocumento}
            onChange={(e) => {
              setTipoDocumento(e.target.value)
              formik.setFieldValue('tomadorCpfCnpj', '')
            }}
            style={{ marginBottom: 8 }}
          >
            <Radio.Button value="cnpj">CNPJ</Radio.Button>
            <Radio.Button value="cpf">CPF</Radio.Button>
          </Radio.Group>
        </div>
        <TextFieldInput
          label={tipoDocumento === 'cnpj' ? 'Tomador CNPJ' : 'Tomador CPF'}
          name="tomadorCpfCnpj"
          mask={tipoDocumento}
          value={formik.values.tomadorCpfCnpj}
          onChange={formik.handleChange}
          loading={loadingCnpj}
          error={formik.errors.tomadorCpfCnpj}
        />
        <TextFieldInput
          label="Tomador Nome"
          name="tomadorNome"
          value={formik.values.tomadorNome}
          onChange={formik.handleChange}
          loading={loadingCnpj}
          error={formik.errors.tomadorNome}
        />
        <TextFieldInput
          label="Valor da Nota Fiscal"
          name="valorNotaFiscal"
          mask="currency"
          value={formik.values.valorNotaFiscal}
          onChange={formik.handleChange}
          error={formik.errors.valorNotaFiscal}
        />
        <div />
        <div className="entire-col">
          <TextFieldInput
            label="Descrição"
            name="descricaoNota"
            value={formik.values.descricaoNota}
            onChange={formik.handleChange}
            error={formik.errors.descricaoNota}
          >
            <Input.TextArea
              name="descricaoNota"
              value={formik.values.descricaoNota}
              onChange={formik.handleChange}
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </TextFieldInput>
        </div>
        <div className="entire-col">
          <TextFieldInput
            label="Informações Complementares"
            name="informacoesComplementares"
            value={formik.values.informacoesComplementares}
            onChange={formik.handleChange}
            error={formik.errors.informacoesComplementares}
          >
            <Input.TextArea
              name="informacoesComplementares"
              value={formik.values.informacoesComplementares}
              onChange={formik.handleChange}
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </TextFieldInput>
        </div>
        <div className="entire-col">
          <h4 style={{ margin: '8px 0' }}>Endereço do Tomador</h4>
        </div>
        <TextFieldInput
          label="CEP"
          name="tomadorEndereco.cep"
          mask="cep"
          value={formik.values.tomadorEndereco.cep}
          onChange={formik.handleChange}
          loading={loadingCep}
        />
        <TextFieldInput
          label="Estado"
          name="tomadorEndereco.estado"
          value={formik.values.tomadorEndereco.estado}
          onChange={formik.handleChange}
          loading={loadingCep}
        />
        <TextFieldInput
          label="Cidade"
          name="tomadorEndereco.cidade"
          value={formik.values.tomadorEndereco.cidade}
          onChange={formik.handleChange}
          loading={loadingCep}
        />
        <TextFieldInput
          label="Bairro"
          name="tomadorEndereco.bairro"
          value={formik.values.tomadorEndereco.bairro}
          onChange={formik.handleChange}
          loading={loadingCep}
        />
        <TextFieldInput
          label="Logradouro"
          name="tomadorEndereco.logradouro"
          value={formik.values.tomadorEndereco.logradouro}
          onChange={formik.handleChange}
          loading={loadingCep}
        />
        <TextFieldInput
          label="Número"
          name="tomadorEndereco.numero"
          value={formik.values.tomadorEndereco.numero}
          onChange={formik.handleChange}
        />
        <div className="entire-col">
          <Checkbox
            name="incluirRetencaoCsrf"
            checked={formik.values.incluirRetencaoCsrf}
            onChange={formik.handleChange}
          >
            Incluir retenção CSRF (PIS 0,65% + COFINS 3% + CSLL 1% = 4,65%)
          </Checkbox>
        </div>
        <div className="entire-col">
          <ButtonAntd
            type="primary"
            htmlType="submit"
            loading={formik.isSubmitting}
            size="large"
            style={{ marginTop: 8 }}
          >
            Emitir Nota Fiscal
          </ButtonAntd>
        </div>
      </FormGrid>
    </FormContainer>
  )
}

const Layout = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  min-height: 100%;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 900px;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const Title = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const SeletorContainer = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SeletorTitle = styled.h3`
  margin: 0;
  color: #494d50;
`

const SeletorRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const MatriculasBadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 32px;
`

const EmptyMatriculas = styled.span`
  color: #999;
  font-size: 13px;
  align-self: center;
`

const MatriculaBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f0f0f0;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 13px;
`

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  font-size: 16px;
  line-height: 1;
  padding: 0;

  &:hover {
    color: #ff4d4f;
  }
`

const FormContainer = styled.form`
  background: #fff;
  border-radius: 10px;
  padding: 20px;
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;

  .entire-col {
    grid-column: 1 / 3;
  }
`
